import { CAlert, CSpinner } from "@coreui/react-pro";
import { useLogto } from "@logto/react";
import { useEffect, useRef, useState } from "react";

import { captureReturnTo } from "../../graphql/policies/auth";

/**
 * OTT invitation landing page. Mounted at `/accept-invite`.
 *
 * Users arrive here via a magic link in an invitation email sent by
 * monitor-api. The URL carries `token` (Logto's one-time-token) and
 * `email` (the invitee's email address, also used as `login_hint`).
 *
 * We hand both off to Logto's SDK via `signIn({ extraParams: {
 * one_time_token, login_hint } })`, matching the shape documented at
 * https://docs.logto.io/end-user-flows/one-time-token and verified
 * against the SDK typings in
 * `@logto/client/lib/client.d.ts` (SignInOptions.extraParams).
 *
 * Logto's experience-side OTT verification route
 * (`packages/core/src/routes/experience/verification-routes/one-time-token.ts`
 * in Logto v1.37.1) consumes the token, creates the user record on
 * first acceptance (interaction event `Register`), attaches the user
 * to `context.jitOrganizationIds` on submit, and redirects back to
 * `/callback` where the existing sign-in exchange completes.
 *
 * We do NOT verify the token client-side. The SDK's redirect kicks
 * the flow off; `/callback` does the exchange. Any token error is
 * surfaced by Logto after the redirect.
 */
const AcceptInvite = () => {
  const { signIn } = useLogto();
  const invokedRef = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (invokedRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const email = params.get("email");

    if (!token || !email) {
      setError(
        "This invitation link appears to be incomplete. Please check the email or ask the person who invited you to send a fresh link.",
      );
      return;
    }

    // Stash the return target so the /callback handoff lands the user
    // on the app root after sign-in completes. Goes through
    // captureReturnTo rather than writing sessionStorage directly: the
    // key moved out of the SDK's namespace in MONITOR-182, and a raw
    // literal here was silently orphaned by that rename.
    captureReturnTo("/");

    invokedRef.current = true;
    signIn({
      redirectUri: `${window.location.origin}/callback`,
      clearTokens: false,
      extraParams: {
        one_time_token: token,
        login_hint: email,
      },
    }).catch((err) => {
      // biome-ignore lint/suspicious/noConsole: deliberate diagnostic for invite sign-in failures
      console.error("AcceptInvite signIn failed:", err);
      setError(
        "We could not start the sign-in flow. Please try the invitation link again, or ask the person who invited you for a fresh link.",
      );
    });
  }, [signIn]);

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          padding: "1rem",
        }}
      >
        <CAlert color="danger" style={{ maxWidth: 480 }}>
          {error}
        </CAlert>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <CSpinner color="primary" /> Redirecting you to sign in…
    </div>
  );
};

export default AcceptInvite;
